import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { ItemScore } from "@/design-system/ItemScore/ItemScore";
import { cn } from "@/lib/utils";
import type { GalleryMilestone } from "@repo/shared/api";
import { grouped } from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import { objectiveReward } from "@repo/shared/utils";
import { Check, Lock, TriangleAlert } from "lucide-react";
import { useEffect, useRef } from "react";

// Below this per-milestone width the track scrolls sideways (5 milestones fit from ~1200px).
const MIN_COLUMN_REM = 15;

/** The Gallery milestones, each with its reward and the score it needs; clicking an unlocked one picks it as the plan's target. */
export const GalleryMilestoneTrack = ({
  milestones,
  level,
  onPick,
}: {
  milestones: GalleryMilestone[];
  level?: number;
  onPick: (level: number) => void;
}) => {
  const { t } = useTranslation("gallery");
  const { t: tObjectives } = useTranslation("objectives");
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrolledToPick = useRef(false);

  // Phones open the track on the picked level, once.
  useEffect(() => {
    // ScrollArea scrolls its viewport, not its root.
    const container = scrollRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    const picked = container?.querySelector<HTMLElement>("[data-picked=true]");
    if (scrolledToPick.current || !container || !picked) return;
    scrolledToPick.current = true;
    container.scrollLeft =
      picked.offsetLeft - (container.clientWidth - picked.offsetWidth) / 2;
  }, [level]);

  return (
    // Phones: scrolls to the screen's edges, the first card still in line with the content.
    <div className="-mx-1.5 max-mobile:-mx-4">
      <ScrollArea
        ref={scrollRef}
        enableShadows
        shadowDirection="x"
        viewportClassName="snap-x snap-mandatory"
      >
        <div
          role="group"
          aria-label={t("levels.pickLevel")}
          className="relative flex pb-1 max-mobile:px-2.5"
          style={{ minWidth: `${milestones.length * MIN_COLUMN_REM}rem` }}
        >
          {milestones.map((milestone) => {
            const reward = objectiveReward(milestone.reward, tObjectives);
            const picked = milestone.level === level;
            // A reached level is no target, and a locked one has no threshold to plan for.
            const locked = milestone.threshold === undefined;
            const pickable = !locked && !milestone.reached;
            const pick = () => {
              if (pickable) onPick(milestone.level);
            };
            return (
              <div
                key={milestone.level}
                data-picked={picked}
                className="flex min-w-0 flex-1 basis-0 snap-center px-1.5"
              >
                {/* Not a button: the estimate's info tip is one. */}
                <div
                  role="button"
                  tabIndex={pickable ? 0 : -1}
                  aria-pressed={picked}
                  aria-disabled={!pickable}
                  onClick={pick}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    pick();
                  }}
                  className={cn(
                    "flex w-full min-w-0 flex-col gap-3 rounded-2xl border p-4 outline-none transition-colors",
                    picked
                      ? "border-primary-action bg-neutral-750"
                      : "border-neutral-700 bg-neutral-800",
                    pickable
                      ? !picked &&
                          "cursor-pointer hover:border-neutral-500 focus-visible:border-neutral-500"
                      : "cursor-default",
                    locked && "opacity-50",
                  )}
                >
                  {/* Like an SBC tile's head: the name, and what it takes on the right. */}
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`text-body-lg ${cn(
                          "font-semibold",
                          milestone.reached
                            ? "text-neutral-400"
                            : "text-neutral-50",
                        )}`}
                      >
                        {t("levels.level", { level: milestone.level })}
                      </span>
                      {milestone.threshold !== undefined && (
                        <span className="flex items-center gap-1.5">
                          <ItemScore
                            size="medium"
                            score={milestone.threshold}
                            estimated={milestone.estimated}
                            className={
                              milestone.reached
                                ? "text-neutral-400"
                                : "text-neutral-50"
                            }
                          />
                          {/* A guessed threshold: the warning beside it explains. */}
                          {milestone.estimated && (
                            <span
                              className="flex"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoTip
                                label={t("levels.track.guessLabel")}
                                width={280}
                                icon={<TriangleAlert className="size-3.5" />}
                                className={
                                  picked
                                    ? "text-amber-400 hover:text-amber-300"
                                    : "text-neutral-500 hover:text-neutral-300"
                                }
                              >
                                {t("levels.track.guess", {
                                  level: milestone.level,
                                  score: grouped.format(milestone.threshold),
                                })}
                              </InfoTip>
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <span className="flex min-h-5 items-center gap-1 text-small">
                      {milestone.reached ? (
                        <span className="flex items-center gap-1 text-complete">
                          <Check className="size-3.5" />
                          {t("levels.track.completed")}
                        </span>
                      ) : locked ? (
                        <span className="flex items-center gap-1 text-neutral-400">
                          <Lock className="size-3.5" />
                          {t("levels.track.locked")}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {/* The reward, like the SBC tile's band: big art, a gold label, the name. */}
                  <div className="flex min-w-0 flex-1 items-center gap-3 border-t border-neutral-700 pt-3">
                    {reward?.rewardImgURL && (
                      <img
                        loading="lazy"
                        className={cn(
                          // Bleeds into the card's padding, so the art reads bigger than the band.
                          "-my-3 -ml-2 h-32 w-24 shrink-0 object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]",
                          milestone.reached && "opacity-60 grayscale",
                        )}
                        alt={reward.name}
                        src={reward.rewardImgURL}
                      />
                    )}
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-card-gold">
                        {t("levels.track.reward")}
                      </span>
                      <span
                        title={reward?.name}
                        className="line-clamp-3 text-xs text-foreground-muted"
                      >
                        {reward?.name}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};
